import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Grid, Card, CardContent,
    Button, Chip, Dialog, IconButton, Accordion, AccordionSummary,
    AccordionDetails, CircularProgress, Fade, Zoom
} from '@mui/material';
import {
    ChevronLeft, ChevronRight, X, PlayCircle, Package, ChevronDown
} from 'lucide-react';
import api from '../utils/api';
import Footer from './Footer';
import LazyImage, { DEFAULT_PLACEHOLDER } from './LazyImage';

const ProductCard = ({ product, onImageClick, onVideoClick }) => {

    return (
        <Zoom in timeout={400}>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    },
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        paddingTop: '75%',
                        cursor: 'pointer',
                        overflow: 'hidden',
                    }}
                    onClick={() => product.images?.length > 0 && onImageClick(product)}
                >
                    <LazyImage
                        src={product.images?.[0]?.url}
                        alt={product.productName}
                        height="100%"
                        showPlaceholderIcon={!product.images?.length}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.5s ease',
                            '&:hover': {
                                transform: 'scale(1.1)',
                            }
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.7))',
                            opacity: 0,
                            transition: 'opacity 0.3s',
                            '&:hover': { opacity: 1 }
                        }}
                    />
                    {product.video && (
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                color: '#1976d2',
                                '&:hover': {
                                    backgroundColor: 'white',
                                    transform: 'translate(-50%, -50%) scale(1.1)',
                                },
                                width: 60,
                                height: 60,
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onVideoClick(product);
                            }}
                        >
                            <PlayCircle size={32} />
                        </IconButton>
                    )}
                    <Chip
                        label={product.netStock > 0 ? 'In Stock' : 'Out of Stock'}
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: product.netStock > 0 ? '#4caf50' : '#f44336',
                            color: 'white',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        }}
                        size="small"
                    />
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 'bold',
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            color: '#1a1a2e',
                        }}
                    >
                        {product.productName}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.6,
                        }}
                    >
                        {product.description || 'Quality product from Ramdayal Rameshwar Lal'}
                    </Typography>
                </CardContent>
            </Card>
        </Zoom>
    );
};

const ImageSlider = ({ images, productName, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const handlePrevious = (e) => {
        e.stopPropagation();
        setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    };

    return (
        <Box sx={{ position: 'relative', bgcolor: 'black', borderRadius: 2, overflow: 'hidden' }}>
            <IconButton
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.6)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                    zIndex: 10,
                }}
            >
                <X />
            </IconButton>
            <Box sx={{ position: 'relative', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                    src={images[currentIndex].url}
                    alt={`${productName} ${currentIndex + 1}`}
                    style={{
                        width: '100%',
                        maxHeight: '80vh',
                        objectFit: 'contain',
                    }}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = DEFAULT_PLACEHOLDER;
                    }}
                />
                {images.length > 1 && (
                    <>
                        <IconButton
                            onClick={handlePrevious}
                            sx={{
                                position: 'absolute',
                                left: 16,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'white',
                                bgcolor: 'rgba(0,0,0,0.6)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)', transform: 'translateY(-50%) scale(1.1)' },
                            }}
                        >
                            <ChevronLeft size={32} />
                        </IconButton>
                        <IconButton
                            onClick={handleNext}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'white',
                                bgcolor: 'rgba(0,0,0,0.6)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)', transform: 'translateY(-50%) scale(1.1)' },
                            }}
                        >
                            <ChevronRight size={32} />
                        </IconButton>
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 20,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                bgcolor: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                px: 3,
                                py: 1,
                                borderRadius: 3,
                                fontWeight: 'bold',
                            }}
                        >
                            {currentIndex + 1} / {images.length}
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

const AllProductsPage = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
    const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
    const [expandedCategory, setExpandedCategory] = useState(false);

    useEffect(() => {
        fetchCategoriesWithProducts();
    }, []);

    const fetchCategoriesWithProducts = async () => {
        setLoading(true);
        try {
            const categoriesResponse = await api.get('/categories');
            const categoriesData = categoriesResponse.data;

            const categoriesWithProducts = await Promise.all(
                categoriesData.map(async (category) => {
                    try {
                        const productsResponse = await api.get(`/products/category/${category._id}`);
                        return {
                            ...category,
                            products: productsResponse.data
                        };
                    } catch (err) {
                        return { ...category, products: [] };
                    }
                })
            );

            setCategories(categoriesWithProducts.filter(cat => cat.products.length > 0));
            setExpandedCategory(categoriesWithProducts[0]?._id || false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const handleImageClick = (product) => {
        if (product.images?.length > 0) {
            setSelectedProduct(product);
            setIsImageDialogOpen(true);
        }
    };

    const handleVideoClick = (product) => {
        if (product.video) {
            setSelectedProduct(product);
            setIsVideoDialogOpen(true);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                py: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}>
                <Container maxWidth="lg">
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate('/')}
                        >
                            <img
                                src="/Brand_logo.svg"
                                alt="Brand Logo"
                                style={{
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    height: '64px',
                                    padding: '4px'
                                }}
                            />
                            <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '1.8rem' }}>
                                Ramdayal Rameshwar Lal
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                sx={{
                                    color: 'white',
                                    borderColor: 'white',
                                    display: { xs: 'none', sm: 'inline-flex' },
                                    '&:hover': {
                                        borderColor: 'white',
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                    }
                                }}
                                onClick={() => navigate('/')}
                            >
                                Home
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    bgcolor: 'white',
                                    color: '#667eea',
                                    '&:hover': {
                                        bgcolor: '#f0f0f0',
                                    }
                                }}
                                onClick={() => navigate('/login')}
                            >
                                Login
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    py: 8,
                    textAlign: 'center',
                }}
            >
                <Container maxWidth="lg">
                    <Fade in timeout={1000}>
                        <Box>
                            <Package size={64} style={{ marginBottom: '16px' }} />
                            <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                                Our Premium Products
                            </Typography>
                            <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.95 }}>
                                Discover our wide range of quality food products. From premium grains to finest spices,
                                we bring you the best nature has to offer.
                            </Typography>
                        </Box>
                    </Fade>
                </Container>
            </Box>

            {/* Products by Category */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                {error ? (
                    <Typography color="error" align="center">{error}</Typography>
                ) : categories.length === 0 ? (
                    <Typography align="center" variant="h6" color="text.secondary">
                        No products available at the moment.
                    </Typography>
                ) : (
                    categories.map((category) => (
                        <Accordion
                            key={category._id}
                            expanded={expandedCategory === category._id}
                            onChange={() => setExpandedCategory(expandedCategory === category._id ? false : category._id)}
                            sx={{
                                mb: 3,
                                borderRadius: 3,
                                overflow: 'hidden',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': {
                                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ChevronDown />}
                                sx={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    minHeight: 80,
                                    '&:hover': {
                                        opacity: 0.95,
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        my: 2,
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    {category.image?.url && (
                                        <LazyImage
                                            src={category.image.url}
                                            alt={category.name}
                                            height={60}
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: 2,
                                                objectFit: 'cover',
                                                border: '3px solid white',
                                            }}
                                        />
                                    )}
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            {category.name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                            {category.products.length} {category.products.length === 1 ? 'product' : 'products'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 4, bgcolor: '#f8f9fa' }}>
                                <Grid container spacing={4}>
                                    {category.products.map((product) => (
                                        <Grid item xs={12} sm={6} md={4} key={product._id}>
                                            <ProductCard
                                                product={product}
                                                onImageClick={handleImageClick}
                                                onVideoClick={handleVideoClick}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
            </Container>

            {/* Image Dialog */}
            <Dialog
                open={isImageDialogOpen}
                onClose={() => setIsImageDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                    }
                }}
            >
                {selectedProduct?.images?.length > 0 && (
                    <ImageSlider
                        images={selectedProduct.images}
                        productName={selectedProduct.productName}
                        onClose={() => setIsImageDialogOpen(false)}
                    />
                )}
            </Dialog>

            {/* Video Dialog */}
            <Dialog
                open={isVideoDialogOpen}
                onClose={() => setIsVideoDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                    }
                }}
            >
                <Box sx={{ position: 'relative', bgcolor: 'black', borderRadius: 2, overflow: 'hidden' }}>
                    <IconButton
                        onClick={() => setIsVideoDialogOpen(false)}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            color: 'white',
                            bgcolor: 'rgba(0,0,0,0.6)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                            zIndex: 10,
                        }}
                    >
                        <X />
                    </IconButton>
                    {selectedProduct?.video && (
                        <video
                            src={selectedProduct.video.url}
                            controls
                            autoPlay
                            style={{ width: '100%', maxHeight: '80vh' }}
                        />
                    )}
                </Box>
            </Dialog>

            <Footer />
        </Box>
    );
};

export default AllProductsPage;
